import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { characters } from "@/mocks/characters";
import { animes } from "@/mocks/animes";

export default function CharactersPage() {
  const [animeFilter, setAnimeFilter] = useState("all");

  useDocumentMeta({
    title: "日漫 - 动漫角色资料大全",
    description:
      "日漫角色资料库收录各部动画的主要角色，包含性格简介、主要故事线、人物关系与所属作品，帮助你更快认识每一个角色。",
    keywords: "动漫角色,角色资料,日本动画角色,日漫",
  });

  const filtered = useMemo(
    () => (animeFilter === "all" ? characters : characters.filter((item) => item.animeId === animeFilter)),
    [animeFilter]
  );

  return (
    <Layout>
      <div className="w-full px-4 md:px-6 pt-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "角色资料" }]} />
      </div>

      <section className="w-full px-4 md:px-6 pt-6 pb-4">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">角色资料库</h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
            汇集站内各动画作品的主要角色，收录性格、故事线与人物关系。选择作品可快速筛选感兴趣的角色。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-16">
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            type="button"
            onClick={() => setAnimeFilter("all")}
            className={`px-4 h-9 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
              animeFilter === "all"
                ? "bg-primary-500 border-primary-500 text-background-50"
                : "border-background-200 text-foreground-700 hover:border-primary-300"
            }`}
          >
            全部作品
          </button>
          {animes.map((anime) => (
            <button
              key={anime.id}
              type="button"
              onClick={() => setAnimeFilter(anime.id)}
              className={`px-4 h-9 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                animeFilter === anime.id
                  ? "bg-primary-500 border-primary-500 text-background-50"
                  : "border-background-200 text-foreground-700 hover:border-primary-300"
              }`}
            >
              {anime.title}
            </button>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((character) => (
              <Link
                key={character.id}
                to={`/character/${character.id}`}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full aspect-square overflow-hidden bg-background-100">
                  <img
                    src={character.avatar}
                    alt={`${character.name} 角色头像`}
                    title={`${character.name} 动漫角色介绍`}
                    className="w-full h-full object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {character.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-foreground-500 line-clamp-1">{character.nameJa}</p>
                  <p className="mt-2 text-xs text-primary-600 flex items-center gap-1 line-clamp-1">
                    <i className="ri-movie-2-line" />
                    {character.animeTitle}
                  </p>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600 line-clamp-2">
                    {character.personality}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState
            title="暂无该作品的角色资料"
            description="换一部作品看看，或浏览全部角色。"
            actionLabel="查看全部角色"
            onAction={() => setAnimeFilter("all")}
          />
        )}
      </section>
    </Layout>
  );
}